// The model catalog: which models an appliance runner can serve, the files they need, where those come from,
// and which hardware layout (profile) each one runs in. The schema lives here because both sides use it: the
// runner plans and generates its llama-swap config from it, the coordinator mirrors the files it hosts itself.
import { z } from "zod";
import { ModelKind, ModelLimits } from "./core";

export const CatalogSource = z
  .object({
    /** "<owner>/<repo>/<revision>/<path>" of a public Hugging Face file. */
    hf: z.string().optional(),
    /** Our own converted weights: the studio mirrors them (GET /api/runner/models/<model>/<file>). */
    coordinator: z.boolean().optional(),
  })
  .refine((s) => !!s.hf || !!s.coordinator, "A catalog file needs a source (hf or coordinator).");
export type CatalogSource = z.infer<typeof CatalogSource>;

export const CatalogFile = z.object({
  id: z.string().min(1),
  /** Where it lands under the models directory (the first segment picks the mirror directory). */
  target: z.string().min(1),
  bytes: z.number().int().positive(),
  /** Known checksum; null means "record what was downloaded". */
  sha256: z.string().nullable().default(null),
  optional: z.boolean().default(false),
  source: CatalogSource,
});
export type CatalogFile = z.infer<typeof CatalogFile>;

export const CatalogRequirements = z.object({
  gpus: z.number().int().min(1),
  /** Memory each of those GPUs needs, in GB. */
  vramGbEach: z.number().positive(),
  /** System RAM in GB (resident H3 needs a lot, the mmap profiles need almost none). */
  ramGb: z.number().positive(),
  diskGb: z.number().positive(),
});
export type CatalogRequirements = z.infer<typeof CatalogRequirements>;

/** One sd-server preset (a model family in sd.cpp's models-config file): role → file id. */
export const CatalogSdPreset = z.object({
  id: z.string(),
  description: z.string().optional(),
  default: z.boolean().default(false),
  files: z.record(z.string(), z.string()),
});
export type CatalogSdPreset = z.infer<typeof CatalogSdPreset>;

export const CatalogProfile = z.object({
  id: z.string().min(1),
  title: z.string().optional(),
  /** File ids this layout needs. */
  files: z.array(z.string()).min(1),
  requires: CatalogRequirements,
  /** What the studio may send to this layout. */
  limits: ModelLimits.default({}),
  env: z.array(z.string()).default([]),
  args: z.string().default(""),
  /** sd-server: options added to every preset of this layout (backend, split_mode, attn_mode…). */
  sdPreset: z.record(z.string(), z.unknown()).optional(),
  /** llama-server: file ids of the weights and of the vision projector. */
  model: z.string().optional(),
  mmproj: z.string().optional(),
  /** audio.cpp: session option → file id. */
  audioSession: z.record(z.string(), z.string()).optional(),
  /** Use only this many of the available GPUs (Music 3 runs on one card). */
  useGpus: z.number().int().min(1).optional(),
  /** Documented but unmeasured settings; not offered to the studio. */
  experimental: z.record(z.string(), z.unknown()).optional(),
  notes: z.string().optional(),
});
export type CatalogProfile = z.infer<typeof CatalogProfile>;

export const CatalogModel = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  kind: ModelKind,
  /** llama-swap aliases (the H3 presets are addressed by name). */
  aliases: z.array(z.string()).default([]),
  runtime: z.enum(["sdcpp", "audiocpp", "llama-server"]),
  checkEndpoint: z.string().optional(),
  notes: z.string().optional(),
  files: z.array(CatalogFile).min(1),
  sdPresets: z.array(CatalogSdPreset).default([]),
  profiles: z.array(CatalogProfile).min(1),
});
export type CatalogModel = z.infer<typeof CatalogModel>;

export const Catalog = z.object({
  version: z.literal(1),
  defaults: z.object({ modelsDir: z.string().default("/models") }).default({ modelsDir: "/models" }),
  /** Named layouts, most capable first (documentation for the profile ids). */
  profiles: z.array(z.object({ id: z.string(), title: z.string() })).default([]),
  models: z.array(CatalogModel).min(1),
});
export type Catalog = z.infer<typeof Catalog>;

/** Parses and cross-checks a catalog (file ids referenced by profiles and presets must exist). */
export function parseCatalogObject(data: unknown): Catalog {
  const parsed = Catalog.safeParse(data);
  if (!parsed.success) {
    const detail = parsed.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`).join("; ");
    throw new Error(`The model catalog is not valid: ${detail}`);
  }
  for (const m of parsed.data.models) {
    const ids = new Set(m.files.map((f) => f.id));
    for (const p of m.profiles) {
      for (const f of p.files)
        if (!ids.has(f)) throw new Error(`${m.id}/${p.id} lists the unknown file ${f}.`);
      for (const f of [p.model, p.mmproj, ...Object.values(p.audioSession ?? {})]) {
        if (f && !ids.has(f)) throw new Error(`${m.id}/${p.id} refers to the unknown file ${f}.`);
      }
    }
    for (const preset of m.sdPresets) {
      for (const f of Object.values(preset.files)) {
        if (!ids.has(f)) throw new Error(`${m.id} preset ${preset.id} uses the unknown file ${f}.`);
      }
    }
  }
  return parsed.data;
}

/** One mirrored file as the studio offers it (GET /api/runner/models). */
export const MirroredFile = z.object({
  model: z.string(),
  file: z.string(),
  target: z.string(),
  /** Size in the catalog. */
  bytes: z.number().int(),
  /** Size on the studio's disk (0 when the file is missing there). */
  size: z.number().int(),
  /** Known or already computed checksum. */
  sha256: z.string().nullable(),
  available: z.boolean(),
});
export type MirroredFile = z.infer<typeof MirroredFile>;
